import React from 'react';
import { motion } from 'framer-motion';
import { CreditCard, Check, Zap, Crown, Star, Calendar, DollarSign } from 'lucide-react';

function Billing() {
  // const { user } = useOutletContext(); // Available for future use
  // const [selectedPlan, setSelectedPlan] = useState('free'); // Available for future use

  const plans = [
    {
      id: 'free',
      name: 'Free',
      price: '$0',
      period: '/month',
      description: 'Perfect for trying out AIRO',
      icon: Zap,
      features: [
        '1,000 API calls/month',
        'Basic analytics',
        'Community support',
        '1 project',
        'Standard response time'
      ],
      current: true
    },
    {
      id: 'pro',
      name: 'Pro',
      price: '$49',
      period: '/month',
      description: 'For growing businesses',
      icon: Star,
      popular: true,
      features: [
        '50,000 API calls/month',
        'Advanced analytics',
        'Priority support',
        'Unlimited projects',
        'Fast response time',
        'Custom integrations'
      ]
    },
    {
      id: 'enterprise',
      name: 'Enterprise',
      price: 'Custom',
      period: '',
      description: 'For large organizations',
      icon: Crown,
      features: [
        'Unlimited API calls',
        'Real-time analytics',
        '24/7 dedicated support',
        'Unlimited projects',
        'Ultra-fast response',
        'Custom integrations',
        'SLA guarantee',
        'On-premise deployment'
      ]
    }
  ];

  const billingHistory = [
    { date: '2025-01-01', description: 'Pro Plan - January 2025', amount: '$49.00', status: 'paid' },
    { date: '2024-12-01', description: 'Pro Plan - December 2024', amount: '$49.00', status: 'paid' },
    { date: '2024-11-01', description: 'Pro Plan - November 2024', amount: '$49.00', status: 'paid' }
  ];

  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h1 className="text-3xl font-bold text-white mb-2">Billing & Subscription</h1>
        <p className="text-gray-400">Manage your subscription and billing information</p>
      </motion.div>

      {/* Current Plan */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-dark-800/50 backdrop-blur-sm border border-white/10 rounded-xl p-6"
      >
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-white mb-2">Current Plan</h2>
            <p className="text-gray-400">You are currently on the <span className="text-primary-400 font-semibold">Free Plan</span></p>
          </div>
          <div className="flex items-center space-x-4">
            <div className="text-right">
              <p className="text-3xl font-bold text-white">$0</p>
              <p className="text-sm text-gray-400">per month</p>
            </div>
          </div>
        </div>

        <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-primary-500/20 rounded-lg flex items-center justify-center">
              <Calendar className="w-6 h-6 text-primary-400" />
            </div>
            <div>
              <p className="text-sm text-gray-400">Next billing date</p>
              <p className="text-white font-semibold">N/A</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-primary-500/20 rounded-lg flex items-center justify-center">
              <Zap className="w-6 h-6 text-primary-400" />
            </div>
            <div>
              <p className="text-sm text-gray-400">API Calls Used</p>
              <p className="text-white font-semibold">342 / 1,000</p>
            </div>
          </div>
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-primary-500/20 rounded-lg flex items-center justify-center">
              <DollarSign className="w-6 h-6 text-primary-400" />
            </div>
            <div>
              <p className="text-sm text-gray-400">Total Spent</p>
              <p className="text-white font-semibold">$0.00</p>
            </div>
          </div>
        </div>
      </motion.div>

      {/* Available Plans */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2 }}
      >
        <h2 className="text-2xl font-bold text-white mb-6">Available Plans</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {plans.map((plan, index) => (
            <motion.div
              key={plan.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 + index * 0.1 }}
              className={`relative bg-dark-800/50 backdrop-blur-sm border rounded-xl p-6 ${
                plan.popular
                  ? 'border-primary-500/50 shadow-lg shadow-primary-500/20'
                  : 'border-white/10'
              }`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-gradient-to-r from-primary-500 to-secondary-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                    MOST POPULAR
                  </span>
                </div>
              )}

              {plan.current && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-green-500 text-white text-xs font-bold px-3 py-1 rounded-full">
                    CURRENT PLAN
                  </span>
                </div>
              )}

              <div className="text-center mb-6">
                <div className={`w-16 h-16 mx-auto mb-4 bg-gradient-to-r ${plan.popular ? 'from-primary-500 to-secondary-500' : 'from-gray-600 to-gray-700'} rounded-full flex items-center justify-center`}>
                  <plan.icon className="w-8 h-8 text-white" />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">{plan.name}</h3>
                <p className="text-gray-400 text-sm mb-4">{plan.description}</p>
                <div className="flex items-baseline justify-center">
                  <span className="text-4xl font-bold text-white">{plan.price}</span>
                  <span className="text-gray-400 ml-1">{plan.period}</span>
                </div>
              </div>

              <ul className="space-y-3 mb-6">
                {plan.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start space-x-3">
                    <Check className="w-5 h-5 text-primary-400 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-300 text-sm">{feature}</span>
                  </li>
                ))}
              </ul>

              <button
                disabled={plan.current}
                className={`w-full py-3 rounded-lg font-semibold transition-all duration-300 ${
                  plan.current
                    ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                    : plan.popular
                    ? 'bg-gradient-to-r from-primary-500 to-secondary-500 text-white hover:shadow-lg'
                    : 'border border-white/20 text-white hover:bg-white/10'
                }`}
              >
                {plan.current ? 'Current Plan' : plan.id === 'enterprise' ? 'Contact Sales' : 'Upgrade'}
              </button>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Billing History */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.7 }}
        className="bg-dark-800/50 backdrop-blur-sm border border-white/10 rounded-xl p-6"
      >
        <h2 className="text-xl font-bold text-white mb-6">Billing History</h2>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-white/10">
                <th className="text-left py-3 px-4 text-gray-400 font-medium">Date</th>
                <th className="text-left py-3 px-4 text-gray-400 font-medium">Description</th>
                <th className="text-left py-3 px-4 text-gray-400 font-medium">Amount</th>
                <th className="text-left py-3 px-4 text-gray-400 font-medium">Status</th>
              </tr>
            </thead>
            <tbody>
              {billingHistory.length === 0 ? (
                <tr>
                  <td colSpan="4" className="text-center py-8 text-gray-400">
                    No billing history yet
                  </td>
                </tr>
              ) : (
                billingHistory.map((item, index) => (
                  <tr key={index} className="border-b border-white/5">
                    <td className="py-4 px-4 text-white">{item.date}</td>
                    <td className="py-4 px-4 text-gray-300">{item.description}</td>
                    <td className="py-4 px-4 text-white font-semibold">{item.amount}</td>
                    <td className="py-4 px-4">
                      <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-500/20 text-green-400">
                        {item.status}
                      </span>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </motion.div>

      {/* Payment Method */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.8 }}
        className="bg-dark-800/50 backdrop-blur-sm border border-white/10 rounded-xl p-6"
      >
        <h2 className="text-xl font-bold text-white mb-6">Payment Method</h2>
        <div className="flex items-center justify-between p-4 bg-dark-700/50 rounded-lg border border-white/10">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-gradient-to-r from-primary-500 to-secondary-500 rounded-lg flex items-center justify-center">
              <CreditCard className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-white font-medium">No payment method added</p>
              <p className="text-sm text-gray-400">Add a payment method to upgrade your plan</p>
            </div>
          </div>
          <button className="bg-gradient-to-r from-primary-500 to-secondary-500 px-6 py-2 rounded-lg font-semibold hover:shadow-lg transition-all duration-300">
            Add Card
          </button>
        </div>
      </motion.div>
    </div>
  );
}

export default Billing;
